/**
 * Sales route factory. The shell spreads these under the authed route:
 *
 *   children: [...salesRoutes(authedRoute)]
 *
 * Pages stay router-free; this file is the only place sales code touches the
 * router and the only place it reads the session. Controls are gated on the
 * exact permission the generated route policy demands — notably the PII
 * reveal, which is never rendered without `sales.order.pii.read`.
 */
import { createRoute, useNavigate, useParams, type AnyRoute } from "@tanstack/react-router";

import { useSession } from "@/lib/session/use-session";

import { OrdersPage } from "./pages/orders-page";
import { OrderDetailPage } from "./pages/order-detail-page";
import { QuoteEditorPage } from "./pages/quote-editor-page";
import { QuotesPage } from "./pages/quotes-page";
import { FulfilmentsPage } from "./pages/fulfilments-page";
import { FulfilmentDetailPage } from "./pages/fulfilment-detail-page";
import { SALES_PERMISSIONS as P, SalesPermissionBoundary } from "./permissions";

function useSalesNav() {
  const navigate = useNavigate();
  return {
    openOrder: (salesOrderId: string) =>
      void navigate({ to: "/sales/orders/$salesOrderId", params: { salesOrderId } } as never),
    openOrders: () => void navigate({ to: "/sales/orders" } as never),
    openQuote: (quoteId: string) =>
      void navigate({ to: "/sales/quotes/$quoteId", params: { quoteId } } as never),
    newQuote: () => void navigate({ to: "/sales/quotes/new" } as never),
    openFulfilment: (fulfilmentId: string) =>
      void navigate({
        to: "/sales/fulfilments/$fulfilmentId",
        params: { fulfilmentId },
      } as never),
    invoiceFulfilment: (fulfilmentId: string) =>
      void navigate({
        to: "/finance/customer-invoices/new",
        search: { shipment: fulfilmentId },
      } as never),
  };
}

function FulfilmentsRoute() {
  const nav = useSalesNav();
  return (
    <SalesPermissionBoundary permission={P.fulfilmentRead}>
      <FulfilmentsPage onOpenFulfilment={nav.openFulfilment} />
    </SalesPermissionBoundary>
  );
}

function FulfilmentDetailRoute() {
  const nav = useSalesNav();
  const { hasPermission, company } = useSession();
  const params = useParams({ strict: false }) as { fulfilmentId?: string };
  return (
    <SalesPermissionBoundary permission={P.fulfilmentRead}>
      <FulfilmentDetailPage
        key={params.fulfilmentId}
        fulfilmentId={params.fulfilmentId ?? ""}
        canExecute={hasPermission(P.fulfilmentExecute)}
        baseCurrency={company?.base_currency ?? "SAR"}
        onOpenOrder={nav.openOrder}
        onInvoice={
          hasPermission("finance.customer-invoice.create") ? nav.invoiceFulfilment : undefined
        }
      />
    </SalesPermissionBoundary>
  );
}

function QuotesRoute() {
  const nav = useSalesNav();
  const { hasPermission } = useSession();
  return (
    <SalesPermissionBoundary permission={P.quoteRead}>
      <QuotesPage
        canWrite={hasPermission(P.quoteWrite)}
        onOpenQuote={nav.openQuote}
        onNewQuote={nav.newQuote}
      />
    </SalesPermissionBoundary>
  );
}

function QuoteEditorRoute() {
  const nav = useSalesNav();
  const { hasPermission, company } = useSession();
  const params = useParams({ strict: false }) as { quoteId?: string };
  // `/sales/quotes/new` and `/sales/quotes/{id}` are the same screen: a quote
  // being written and one being read are the same document at two moments.
  const quoteId = params.quoteId === "new" ? undefined : params.quoteId;
  return (
    <SalesPermissionBoundary permission={P.quoteRead}>
      <QuoteEditorPage
        key={params.quoteId}
        quoteId={quoteId}
        defaultCurrency={company?.base_currency ?? "SAR"}
        canWrite={hasPermission(P.quoteWrite)}
        canConvert={hasPermission(P.quoteConvert)}
        onSaved={nav.openQuote}
        onOpenOrder={nav.openOrder}
      />
    </SalesPermissionBoundary>
  );
}

function OrdersRoute() {
  const nav = useSalesNav();
  const { hasPermission } = useSession();
  return (
    <SalesPermissionBoundary permission={P.orderRead}>
      <OrdersPage
        canReadChannels={hasPermission("channels.channel.read")}
        onOpenOrder={nav.openOrder}
      />
    </SalesPermissionBoundary>
  );
}

function OrderDetailRoute() {
  const nav = useSalesNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { salesOrderId?: string };
  return (
    <SalesPermissionBoundary permission={P.orderRead}>
      <OrderDetailPage
        key={params.salesOrderId}
        salesOrderId={params.salesOrderId ?? ""}
        canReadPii={hasPermission(P.orderPiiRead)}
        canReadChannels={hasPermission("channels.channel.read")}
        canAllocate={hasPermission(P.fulfilmentAllocate)}
        onBack={nav.openOrders}
        onOpenFulfilment={nav.openFulfilment}
      />
    </SalesPermissionBoundary>
  );
}

export function salesRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    createRoute({
      getParentRoute: () => parent,
      path: "sales/quotes",
      component: QuotesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "sales/quotes/$quoteId",
      component: QuoteEditorRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "sales/orders",
      component: OrdersRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "sales/fulfilments",
      component: FulfilmentsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "sales/fulfilments/$fulfilmentId",
      component: FulfilmentDetailRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "sales/orders/$salesOrderId",
      component: OrderDetailRoute,
    }),
  ] as AnyRoute[];
}
